import { useEffect, useState } from "react";
import { api, API_BASE } from "@/lib/api";
import { getToken } from "@/lib/auth";
import { ITMS } from "@/constants/testIds";
import { toast } from "sonner";
import { FilePdf, FileXls, Printer, EnvelopeSimple, Download, MapPinLine, Globe, Copy, X } from "@phosphor-icons/react";
import { Button } from "@/components/ui/button";
import { Input } from "@/components/ui/input";
import {
  Dialog, DialogContent, DialogHeader, DialogTitle,
} from "@/components/ui/dialog";
import {
  LineChart, Line, BarChart, Bar, ResponsiveContainer, XAxis, YAxis, Tooltip,
  CartesianGrid, AreaChart, Area,
} from "recharts";

const CATEGORIES = [
  "Operational", "Fleet", "Revenue", "Ticketing", "Passenger", "Crew",
  "Maintenance", "Private Operator", "GPS", "Battery", "Audit", "Security", "Financial",
];

export default function Reports() {
  const [data, setData] = useState({});
  const [cat, setCat] = useState("Revenue");
  const [from, setFrom] = useState("2026-01-01");
  const [to, setTo] = useState("2026-02-15");
  const [granularity, setGranularity] = useState("month");
  const [chartsLoading, setChartsLoading] = useState(false);
  const [gtfs, setGtfs] = useState(null);
  const [gtfsLoading, setGtfsLoading] = useState(false);
  const [busy, setBusy] = useState(false);
  const [emailOpen, setEmailOpen] = useState(false);
  // Filters
  const [filterOptions, setFilterOptions] = useState({});
  const [filters, setFilters] = useState({ depot: "", bus_type: "", operator: "", route: "", crew: "", status: "" });

  // Load filter options once
  useEffect(() => {
    api.get("/reports/filter-options").then(({ data }) => setFilterOptions(data)).catch(() => {});
  }, []);

  // Build query string of all the active filters
  const filterQs = Object.entries(filters)
    .filter(([, v]) => v)
    .map(([k, v]) => `${k}=${encodeURIComponent(v)}`)
    .join("&");

  // Re-fetch dashboard charts whenever date range / granularity / filters change
  useEffect(() => {
    let alive = true;
    setChartsLoading(true);
    const qs = `date_from=${from}&date_to=${to}&granularity=${granularity}${filterQs ? "&" + filterQs : ""}`;
    api.get(`/reports/summary?${qs}`)
      .then(({ data }) => { if (alive) setData(data); })
      .catch(() => { if (alive) toast.error("Failed to refresh charts"); })
      .finally(() => { if (alive) setChartsLoading(false); });
    return () => { alive = false; };
  }, [from, to, granularity, filterQs]);
  useEffect(() => { api.get("/open/gtfs/validate").then(({data}) => setGtfs(data)).catch(() => {}); }, []);

  // Auto-pick a sensible default range when granularity changes (and the user hasn't customised dates yet)
  const setPreset = (g) => {
    setGranularity(g);
    const today = new Date();
    const fmt = (d) => d.toISOString().slice(0, 10);
    const to_ = new Date(today);
    let from_ = new Date(today);
    if (g === "day") from_.setDate(today.getDate() - 29);          // last 30 days
    else if (g === "week") from_.setDate(today.getDate() - 7 * 11); // last 12 weeks
    else if (g === "month") from_.setMonth(today.getMonth() - 6);   // last 7 months
    else if (g === "year") from_.setFullYear(today.getFullYear() - 4); // last 5 years
    setFrom(fmt(from_));
    setTo(fmt(to_));
  };

  const exportReport = async (format) => {
    if (busy) return;
    setBusy(true);
    try {
      const url = `${API_BASE}/reports/export?category=${encodeURIComponent(cat)}&format=${format}&date_from=${from}&date_to=${to}&granularity=${granularity}${filterQs ? "&" + filterQs : ""}`;
      const resp = await fetch(url, { headers: { Authorization: `Bearer ${getToken()}` } });
      if (!resp.ok) throw new Error(`HTTP ${resp.status}`);
      const blob = await resp.blob();
      const link = document.createElement("a");
      const safeCat = cat.replace(/\W+/g, "_");
      const ext = format === "xlsx" ? "xlsx" : format;
      link.href = URL.createObjectURL(blob);
      link.download = `ktcl_${safeCat}_${from}_${to}.${ext}`;
      document.body.appendChild(link);
      link.click();
      link.remove();
      setTimeout(() => URL.revokeObjectURL(link.href), 1500);
      toast.success(`${cat} ${format.toUpperCase()} downloaded`);
    } catch (e) {
      toast.error(`Export failed: ${e.message}`);
    } finally {
      setBusy(false);
    }
  };

  const printReport = async () => {
    if (busy) return;
    setBusy(true);
    try {
      const url = `${API_BASE}/reports/export?category=${encodeURIComponent(cat)}&format=pdf&date_from=${from}&date_to=${to}&granularity=${granularity}${filterQs ? "&" + filterQs : ""}`;
      const resp = await fetch(url, { headers: { Authorization: `Bearer ${getToken()}` } });
      if (!resp.ok) throw new Error(`HTTP ${resp.status}`);
      const blob = await resp.blob();
      const blobUrl = URL.createObjectURL(blob);
      const w = window.open(blobUrl, "_blank");
      if (w) {
        w.addEventListener("load", () => { try { w.print(); } catch {} }, { once: true });
        toast.success("Print preview opened");
      } else {
        toast.error("Pop-up blocked — please allow pop-ups to print");
      }
      setTimeout(() => URL.revokeObjectURL(blobUrl), 60000);
    } catch (e) {
      toast.error(`Print failed: ${e.message}`);
    } finally {
      setBusy(false);
    }
  };

  const handleExport = (kind) => {
    if (kind === "PDF") return exportReport("pdf");
    if (kind === "Excel") return exportReport("xlsx");
    if (kind === "CSV") return exportReport("csv");
    if (kind === "Print") return printReport();
    if (kind === "Email") return setEmailOpen(true);
  };

  const publicGtfsUrl = `${API_BASE}/open/gtfs.zip`;

  const downloadGtfs = () => {
    setGtfsLoading(true);
    // Trigger anonymous download via a temporary link - public endpoint requires no auth
    const a = document.createElement("a");
    a.href = publicGtfsUrl;
    a.download = "ktcl_gtfs.zip";
    a.click();
    setTimeout(() => setGtfsLoading(false), 1200);
    toast.success("GTFS feed download started");
  };

  const copyGtfsUrl = async () => {
    try {
      await navigator.clipboard.writeText(publicGtfsUrl);
      toast.success("Public GTFS URL copied — share with Google/Moovit");
    } catch {
      toast.error("Clipboard blocked - URL: " + publicGtfsUrl);
    }
  };

  const refreshPreview = async () => {
    try {
      const { data } = await api.get("/open/gtfs/validate");
      setGtfs(data);
      toast.success(`Feed ready · ${data.counts.routes} routes · ${data.counts.trips} trips`);
    } catch (e) {
      toast.error("Failed to refresh feed preview");
    }
  };

  return (
    <div data-testid={ITMS.reportsRoot} className="p-4 lg:p-6 space-y-5 lg:space-y-6">
      <div className="flex items-start justify-between gap-3 flex-wrap">
        <div>
          <div className="label-cap text-slate-500">Reporting Center</div>
          <h1 className="font-display text-2xl lg:text-3xl font-bold leading-tight">Enterprise Reports</h1>
        </div>
        <div className="flex items-center gap-1.5 sm:gap-2 flex-wrap">
          {[
            { l: "PDF", i: FilePdf, c: "#EF4444", tid: "report-export-pdf" },
            { l: "Excel", i: FileXls, c: "#22C55E", tid: "report-export-excel" },
            { l: "CSV", i: Download, c: "#64748B", tid: "report-export-csv" },
            { l: "Print", i: Printer, c: "#1E3A8A", tid: "report-export-print" },
            { l: "Email", i: EnvelopeSimple, c: "#8B5CF6", tid: "report-export-email" },
          ].map((b) => (
            <button key={b.l} onClick={() => handleExport(b.l)} disabled={busy}
              data-testid={b.tid}
              className="flex items-center gap-1.5 px-3 py-1.5 border border-slate-200 rounded-md text-xs hover:bg-slate-50 transition-colors disabled:opacity-60">
              <b.i size={14} style={{ color: b.c }}/> {b.l}
            </button>
          ))}
        </div>
      </div>

      {/* Open Data: GTFS feed for Google Maps / Moovit / Apple Maps */}
      <div data-testid={ITMS.gtfsCard}
        className="ktcl-card p-4 lg:p-5 border-l-4 border-emerald-500 bg-gradient-to-br from-emerald-50/60 to-white">
        <div className="flex items-start justify-between gap-4 flex-wrap">
          <div className="flex items-start gap-3 min-w-0 flex-1">
            <div className="shrink-0 w-11 h-11 rounded-lg bg-emerald-600 text-white flex items-center justify-center">
              <Globe size={22} weight="bold"/>
            </div>
            <div className="min-w-0">
              <div className="flex items-center gap-2 flex-wrap">
                <h3 className="font-display font-bold text-base lg:text-lg">Open Public GTFS Feed</h3>
                <span className="px-2 py-0.5 rounded text-[10px] font-semibold tracking-wide uppercase bg-emerald-100 text-emerald-700">
                  Tier 1 · Open Data
                </span>
              </div>
              <p className="text-xs lg:text-sm text-slate-600 mt-0.5 leading-snug">
                A live GTFS-Static feed of KTCL schedules. Submit this URL to <b>Google Maps</b>, <b>Apple Maps</b>, <b>Moovit</b>, and <b>Transit App</b> so passengers can see KTCL buses natively. No auth required.
              </p>
              <div className="mt-2 flex items-center gap-2 flex-wrap text-xs">
                <code className="px-2 py-1 bg-slate-900 text-emerald-300 rounded font-mono break-all text-[11px]">
                  {publicGtfsUrl}
                </code>
              </div>
            </div>
          </div>

          <div className="flex flex-col gap-1.5 shrink-0">
            <button data-testid={ITMS.gtfsDownloadBtn} onClick={downloadGtfs} disabled={gtfsLoading}
              className="flex items-center gap-2 px-3.5 py-2 rounded-md bg-emerald-600 hover:bg-emerald-700 text-white text-xs font-semibold transition-colors disabled:opacity-60">
              <Download size={14} weight="bold"/> {gtfsLoading ? "Preparing..." : "Download gtfs.zip"}
            </button>
            <button data-testid={ITMS.gtfsCopyUrlBtn} onClick={copyGtfsUrl}
              className="flex items-center gap-2 px-3.5 py-2 rounded-md border border-emerald-600 text-emerald-700 hover:bg-emerald-50 text-xs font-semibold transition-colors">
              <Copy size={14}/> Copy Public URL
            </button>
            <button data-testid={ITMS.gtfsPreviewBtn} onClick={refreshPreview}
              className="flex items-center gap-2 px-3.5 py-2 rounded-md text-emerald-700 hover:bg-emerald-50 text-xs font-semibold transition-colors">
              <MapPinLine size={14}/> Refresh Preview
            </button>
          </div>
        </div>

        {gtfs && (
          <div className="mt-4 grid grid-cols-2 md:grid-cols-4 lg:grid-cols-7 gap-2.5">
            {[
              { k: "Stops", v: gtfs.counts.stops },
              { k: "Routes", v: gtfs.counts.routes },
              { k: "Trips", v: gtfs.counts.trips },
              { k: "Stop Times", v: gtfs.counts.stop_times },
              { k: "Shapes", v: gtfs.counts.shapes },
              { k: "Services", v: gtfs.counts.calendar_services },
              { k: "Size", v: `${(gtfs.size_bytes / 1024).toFixed(1)} KB` },
            ].map((m) => (
              <div key={m.k} className="bg-white rounded-md border border-slate-200 px-3 py-2">
                <div className="text-[10px] uppercase tracking-wide text-slate-500 font-semibold">{m.k}</div>
                <div className="text-base font-bold font-display text-slate-900 leading-tight">{m.v}</div>
              </div>
            ))}
          </div>
        )}

        {gtfs && (
          <div className="mt-3 flex items-center gap-2 flex-wrap text-[11px] text-slate-500">
            <span>Feed v{gtfs.feed_version}</span>
            <span>·</span>
            <span>Valid {gtfs.start_date} → {gtfs.end_date}</span>
            {gtfs.counts.routes_skipped > 0 && (
              <>
                <span>·</span>
                <span className="text-amber-600">
                  {gtfs.counts.routes_skipped} route(s) skipped (map stops in Route Designer to include)
                </span>
              </>
            )}
          </div>
        )}
      </div>

      <div className="grid grid-cols-12 gap-4">
        {/* Filters */}
        <aside className="col-span-12 lg:col-span-3 ktcl-card p-4 space-y-4">
          <div>
            <div className="label-cap text-slate-500 mb-2">Categories</div>
            <div className="space-y-1">
              {CATEGORIES.map((c) => (
                <button key={c} onClick={() => setCat(c)}
                  className={`w-full text-left px-3 py-1.5 rounded text-sm transition-colors ${cat===c?"bg-[#1E3A8A] text-white font-semibold":"hover:bg-slate-100"}`}>
                  {c} Reports
                </button>
              ))}
            </div>
          </div>
          <div>
            <div className="label-cap text-slate-500 mb-2">Date Range</div>
            <div className="grid grid-cols-4 gap-1 mb-2.5" role="tablist" aria-label="Date granularity">
              {[
                { k: "day",   l: "Day" },
                { k: "week",  l: "Week" },
                { k: "month", l: "Month" },
                { k: "year",  l: "Year" },
              ].map((g) => (
                <button key={g.k} onClick={() => setPreset(g.k)}
                  data-testid={`report-gran-${g.k}`}
                  role="tab" aria-selected={granularity === g.k}
                  className={`text-[11.5px] font-semibold rounded-md py-1.5 transition-colors
                    ${granularity === g.k
                      ? "bg-[#1E3A8A] text-white shadow-sm"
                      : "bg-slate-50 text-slate-600 border border-slate-200 hover:bg-slate-100"}`}>
                  {g.l}
                </button>
              ))}
            </div>
            <div className="space-y-2">
              <input type="date" value={from} onChange={e=>setFrom(e.target.value)}
                data-testid="report-date-from"
                className="w-full px-3 py-1.5 bg-slate-50 border border-slate-200 rounded text-sm outline-none"/>
              <input type="date" value={to} onChange={e=>setTo(e.target.value)}
                data-testid="report-date-to"
                className="w-full px-3 py-1.5 bg-slate-50 border border-slate-200 rounded text-sm outline-none"/>
            </div>
            <div className="mt-1.5 text-[10.5px] text-slate-500 flex items-center gap-1">
              {chartsLoading
                ? <><span className="inline-block w-1.5 h-1.5 rounded-full bg-amber-500 animate-pulse"/> Refreshing charts…</>
                : <><span className="inline-block w-1.5 h-1.5 rounded-full bg-emerald-500"/> {data.buckets ?? 0} {granularity}-buckets · {data.date_from} → {data.date_to}</>
              }
            </div>
          </div>
          <div>
            <div className="flex items-center justify-between mb-2">
              <div className="label-cap text-slate-500">Filters</div>
              {filterQs && (
                <button onClick={() => setFilters({ depot: "", bus_type: "", operator: "", route: "", crew: "", status: "" })}
                  data-testid="report-filter-reset"
                  className="text-[10px] font-semibold text-[#1E3A8A] hover:underline">
                  Reset all
                </button>
              )}
            </div>
            {[
              { k: "depot",    label: "Depot",    placeholder: "All Depots" },
              { k: "bus_type", label: "Bus Type", placeholder: "All Bus Types" },
              { k: "operator", label: "Operator", placeholder: "All Operators" },
              { k: "route",    label: "Route",    placeholder: "All Routes" },
              { k: "crew",     label: "Crew Role",placeholder: "All Crew Roles" },
              { k: "status",   label: "Status",   placeholder: "All Statuses" },
            ].map((f) => (
              <select key={f.k}
                value={filters[f.k]}
                onChange={(e) => setFilters((cur) => ({ ...cur, [f.k]: e.target.value }))}
                data-testid={`report-filter-${f.k}`}
                aria-label={`${f.label} filter`}
                className={`w-full px-3 py-1.5 border rounded text-sm outline-none mt-1.5 transition-colors
                  ${filters[f.k]
                    ? "bg-[#EEF2FF] border-[#1E3A8A] font-semibold text-[#1E3A8A]"
                    : "bg-slate-50 border-slate-200"}`}>
                <option value="">{f.placeholder}</option>
                {(filterOptions[f.k] || []).map((opt) => (
                  <option key={opt} value={opt}>{opt}</option>
                ))}
              </select>
            ))}
            {filterQs && (
              <div className="mt-2 text-[10.5px] text-emerald-700 font-semibold bg-emerald-50 border border-emerald-200 rounded px-2 py-1.5"
                data-testid="report-filters-applied-count">
                {Object.values(filters).filter(Boolean).length} filter(s) applied — charts &amp; exports are scoped accordingly
              </div>
            )}
          </div>
        </aside>

        {/* Body */}
        <div className="col-span-12 lg:col-span-9 space-y-4">
          <div className="grid grid-cols-1 md:grid-cols-2 gap-4">
            <ChartCard title="Revenue Trend" subtitle="Last 7 months · ₹ in lakhs">
              <ResponsiveContainer width="100%" height={220}>
                <AreaChart data={(data.revenue_trend || []).map(d => ({ ...d, value: d.value/100000 }))}>
                  <defs><linearGradient id="rg" x1="0" y1="0" x2="0" y2="1">
                    <stop offset="0%" stopColor="#1E3A8A" stopOpacity={0.4}/>
                    <stop offset="100%" stopColor="#1E3A8A" stopOpacity={0}/>
                  </linearGradient></defs>
                  <CartesianGrid strokeDasharray="3 3" stroke="#E2E8F0"/>
                  <XAxis dataKey="month" tick={{ fontSize: 11 }} stroke="#94A3B8"/>
                  <YAxis tick={{ fontSize: 11 }} stroke="#94A3B8"/>
                  <Tooltip contentStyle={{ fontSize: 12, borderRadius: 6 }}/>
                  <Area type="monotone" dataKey="value" stroke="#1E3A8A" fill="url(#rg)" strokeWidth={2}/>
                </AreaChart>
              </ResponsiveContainer>
            </ChartCard>

            <ChartCard title="Passenger Trend" subtitle="Monthly footfall">
              <ResponsiveContainer width="100%" height={220}>
                <LineChart data={data.passenger_trend || []}>
                  <CartesianGrid strokeDasharray="3 3" stroke="#E2E8F0"/>
                  <XAxis dataKey="month" tick={{ fontSize: 11 }} stroke="#94A3B8"/>
                  <YAxis tick={{ fontSize: 11 }} stroke="#94A3B8"/>
                  <Tooltip contentStyle={{ fontSize: 12, borderRadius: 6 }}/>
                  <Line type="monotone" dataKey="value" stroke="#22C55E" strokeWidth={2}/>
                </LineChart>
              </ResponsiveContainer>
            </ChartCard>

            <ChartCard title="Trip Trend" subtitle="Completed trips">
              <ResponsiveContainer width="100%" height={220}>
                <BarChart data={data.trip_trend || []}>
                  <CartesianGrid strokeDasharray="3 3" stroke="#E2E8F0" vertical={false}/>
                  <XAxis dataKey="month" tick={{ fontSize: 11 }} stroke="#94A3B8"/>
                  <YAxis tick={{ fontSize: 11 }} stroke="#94A3B8"/>
                  <Tooltip contentStyle={{ fontSize: 12, borderRadius: 6 }}/>
                  <Bar dataKey="value" fill="#1E3A8A" radius={[4,4,0,0]}/>
                </BarChart>
              </ResponsiveContainer>
            </ChartCard>

            <ChartCard title="Utilization %" subtitle="Fleet productivity">
              <ResponsiveContainer width="100%" height={220}>
                <LineChart data={data.utilization_trend || []}>
                  <CartesianGrid strokeDasharray="3 3" stroke="#E2E8F0"/>
                  <XAxis dataKey="month" tick={{ fontSize: 11 }} stroke="#94A3B8"/>
                  <YAxis tick={{ fontSize: 11 }} stroke="#94A3B8" unit="%"/>
                  <Tooltip contentStyle={{ fontSize: 12, borderRadius: 6 }}/>
                  <Line type="monotone" dataKey="value" stroke="#F97316" strokeWidth={2}/>
                </LineChart>
              </ResponsiveContainer>
            </ChartCard>

            <ChartCard title="Delay (min) Trend" subtitle="Average delay per trip">
              <ResponsiveContainer width="100%" height={220}>
                <BarChart data={data.delay_trend || []}>
                  <CartesianGrid strokeDasharray="3 3" stroke="#E2E8F0" vertical={false}/>
                  <XAxis dataKey="month" tick={{ fontSize: 11 }} stroke="#94A3B8"/>
                  <YAxis tick={{ fontSize: 11 }} stroke="#94A3B8"/>
                  <Tooltip contentStyle={{ fontSize: 12, borderRadius: 6 }}/>
                  <Bar dataKey="value" fill="#F97316" radius={[4,4,0,0]}/>
                </BarChart>
              </ResponsiveContainer>
            </ChartCard>

            <ChartCard title="Breakdown Trend" subtitle="Incidents reported">
              <ResponsiveContainer width="100%" height={220}>
                <BarChart data={data.breakdown_trend || []}>
                  <CartesianGrid strokeDasharray="3 3" stroke="#E2E8F0" vertical={false}/>
                  <XAxis dataKey="month" tick={{ fontSize: 11 }} stroke="#94A3B8"/>
                  <YAxis tick={{ fontSize: 11 }} stroke="#94A3B8"/>
                  <Tooltip contentStyle={{ fontSize: 12, borderRadius: 6 }}/>
                  <Bar dataKey="value" fill="#EF4444" radius={[4,4,0,0]}/>
                </BarChart>
              </ResponsiveContainer>
            </ChartCard>
          </div>
        </div>
      </div>

      {/* Email Report Modal */}
      <EmailReportDialog open={emailOpen} onOpenChange={setEmailOpen}
        category={cat} dateFrom={from} dateTo={to} granularity={granularity}/>
    </div>
  );
}

function EmailReportDialog({ open, onOpenChange, category, dateFrom, dateTo, granularity }) {
  const [toEmail, setToEmail] = useState("");
  const [cc, setCc] = useState("");
  const [format, setFormat] = useState("pdf");
  const [notes, setNotes] = useState("");
  const [busy, setBusy] = useState(false);

  const send = async () => {
    if (!toEmail || !toEmail.includes("@")) {
      toast.error("Enter a valid recipient email"); return;
    }
    setBusy(true);
    try {
      await api.post("/reports/email", {
        category, format, date_from: dateFrom, date_to: dateTo, granularity,
        to_email: toEmail, cc, notes,
      });
      toast.success(`${category} ${format.toUpperCase()} queued to ${toEmail}`);
      onOpenChange(false);
      setToEmail(""); setCc(""); setNotes("");
    } catch (e) {
      toast.error(e?.response?.data?.detail || "Send failed");
    } finally { setBusy(false); }
  };

  return (
    <Dialog open={open} onOpenChange={onOpenChange}>
      <DialogContent className="max-w-md" data-testid="report-email-dialog">
        <DialogHeader>
          <DialogTitle className="flex items-center gap-2">
            <EnvelopeSimple size={18}/> Email Report
          </DialogTitle>
          <div className="text-xs text-muted-foreground mt-1">
            Sends <b>{category}</b> for <b>{dateFrom} → {dateTo}</b> as an attachment.
            <span className="ml-1 inline-block px-1.5 py-0.5 rounded bg-amber-100 text-amber-800 font-bold text-[10px]">MOCKED</span>
          </div>
        </DialogHeader>
        <div className="space-y-3">
          <div>
            <label className="text-[11px] font-bold uppercase tracking-wider text-slate-500">Format</label>
            <div className="flex gap-1 mt-1">
              {["pdf", "xlsx", "csv"].map((f) => (
                <button key={f} type="button" onClick={() => setFormat(f)}
                  data-testid={`report-email-fmt-${f}`}
                  className={`px-3 py-1 rounded text-xs font-semibold border
                    ${format === f ? "bg-[#1E3A8A] text-white border-[#1E3A8A]" : "bg-white border-slate-200 hover:bg-slate-50"}`}>
                  {f.toUpperCase()}
                </button>
              ))}
            </div>
          </div>
          <div>
            <label className="text-[11px] font-bold uppercase tracking-wider text-slate-500" htmlFor="to-email">To</label>
            <Input id="to-email" type="email" value={toEmail} onChange={(e) => setToEmail(e.target.value)}
              placeholder="ops@ktcl.gov.in" data-testid="report-email-to"/>
          </div>
          <div>
            <label className="text-[11px] font-bold uppercase tracking-wider text-slate-500" htmlFor="cc-email">CC (optional)</label>
            <Input id="cc-email" type="email" value={cc} onChange={(e) => setCc(e.target.value)}
              placeholder="director@ktcl.gov.in" data-testid="report-email-cc"/>
          </div>
          <div>
            <label className="text-[11px] font-bold uppercase tracking-wider text-slate-500" htmlFor="notes">Notes (optional)</label>
            <Input id="notes" value={notes} onChange={(e) => setNotes(e.target.value)}
              placeholder="Daily snapshot for management review"
              data-testid="report-email-notes"/>
          </div>
        </div>
        <div className="flex justify-end gap-2 mt-2">
          <Button variant="outline" onClick={() => onOpenChange(false)} data-testid="report-email-cancel">
            <X size={14} className="mr-1"/> Cancel
          </Button>
          <Button onClick={send} disabled={busy} data-testid="report-email-send"
            className="bg-[#1E3A8A] hover:bg-[#1E3A8A]/90">
            <EnvelopeSimple size={14} className="mr-1"/> {busy ? "Queuing…" : "Send"}
          </Button>
        </div>
      </DialogContent>
    </Dialog>
  );
}

const ChartCard = ({ title, subtitle, children }) => (
  <div className="ktcl-card p-4">
    <div className="label-cap text-slate-500">{subtitle}</div>
    <h3 className="font-display font-bold text-lg mb-2">{title}</h3>
    {children}
  </div>
);
